import { HubConnectionBuilder, HubConnection } from '@microsoft/signalr';
import equal from 'fast-deep-equal';
import { differingFields } from './state-diff';

import { get, buildUrl } from '../util/fetch';
import { Game } from './game';
import { GameEvent, GameUiModule } from './game-module';

const ACTION_TIMEOUT = 5000;

/**
 * The engine-level event the server sends when a client (re)joins a game, so
 * that the client knows to (re)load the game's view.
 */
const RECONNECT_EVENT = 'Reconnect';

/**
 * What a game sends when an action changed something only some players may see.
 * Events are broadcast to the whole group, so the change cannot travel in one;
 * every client re-reads its own view instead, which is per-player (ADR-0002).
 */
const REFRESH_EVENT = 'Refresh';

type ResolveReject = [() => void, (err: string) => void];

export type GameSubscriber = (
  game: unknown,
  events: GameEvent[],
  error?: string,
) => void;

/**
 * Owns the single SignalR connection to `/game/hub`: it (re)connects, fans the
 * events it receives out to the subscribers of the game they belong to, and
 * delegates reducing them to that game's registered module.
 */
export class GameHubService {
  private connection: HubConnection;

  private subscribers: GameSubscriber[] = [];

  private modules: { [gameId: string]: GameUiModule<unknown> } = {};

  private gameState: { [id: string]: unknown } = {};

  private connectionInitialisation?: Promise<void>;

  private gameInitialised = false;
  private gameInitialisation?: Promise<void>;

  private resolveExecutes: ResolveReject[] = [];

  constructor() {
    this.connection = this.buildConnection();
  }

  private buildConnection(): HubConnection {
    const connection = new HubConnectionBuilder()
      .withAutomaticReconnect()
      .withUrl(buildUrl('/game/hub'))
      .build();

    connection.on('ReceiveEvents', (events: GameEvent[]) =>
      this.handleReceiveEvents(events),
    );

    connection.on('ReceiveActionErrorResponse', (err) => {
      console.error(err);
      this.resolveExecutes.forEach(([_, reject]) => reject(err));
      this.resolveExecutes = [];
    });

    connection.onreconnected(() => {
      for (const gameId in this.gameState) {
        this.reconnectToGame(gameId);
      }
    });

    return connection;
  }

  private async restartConnection(): Promise<void> {
    try {
      await this.connection.stop();
      this.connection = this.buildConnection();
      this.connectionInitialisation = undefined;
      await this.initialiseConnection();
      for (const gameId in this.gameState) {
        this.reconnectToGame(gameId);
      }
    } catch {}
  }

  public getGameByLookup(lookup: string): Promise<Game> {
    return get<Game>(`/game/lookup/${lookup}`);
  }

  public registerGame(gameId: string, module: GameUiModule<unknown>): void {
    this.modules[gameId] = module;
  }

  /**
   * Starts the connection on first use rather than at construction: this
   * singleton is part of the initial bundle, so it exists before the player
   * has signed in, and a connection opened then would be anonymous.
   */
  public initialiseConnection = (): Promise<void> => {
    if (!this.connectionInitialisation) {
      this.connectionInitialisation = this.connection.start();
    }

    return this.connectionInitialisation;
  };

  public joinGame = async (gameId: string, playerName: string) => {
    await this.connection.send('JoinGame', gameId, playerName);
  };

  public reconnectToGame = async (gameId: string) => {
    await this.connection.send('Reconnect', gameId);
  };

  public subscribeToUpdates = (callback: GameSubscriber) => {
    this.subscribers.push(callback);
    return callback;
  };

  public unsubscribeToUpdates = (callback: GameSubscriber) => {
    const index = this.subscribers.indexOf(callback);
    if (index > -1) {
      this.subscribers.splice(index, 1);
    }
  };

  public executeAction = async (action: unknown): Promise<void> => {
    return new Promise((resolve, reject) => {
      try {
        const timeout = setTimeout(async () => {
          console.warn('Action timed out, restarting connection...');
          await this.restartConnection();
          console.log('Restarted connection, retrying action...');
          this.connection.send('ExecuteAction', action);
        }, ACTION_TIMEOUT);

        const resolveAndClearTimeout = () => {
          clearTimeout(timeout);
          resolve();
        };

        // A refusal clears the timeout too: it used to be left armed, so a
        // refused action tore the connection down five seconds later and was
        // sent again.
        this.resolveExecutes.push([
          resolveAndClearTimeout,
          (err: string) => {
            clearTimeout(timeout);
            reject(err);
          },
        ]);
        this.connection.send('ExecuteAction', action);
      } catch (err) {
        reject(err);
      }
    });
  };

  private async loadGameStateAfterConnect(gameId: string, events: GameEvent[]) {
    const module = this.modules[gameId];
    if (!module) {
      return;
    }

    let resolveInitialisation: () => void = () => {};
    this.gameInitialisation = new Promise((resolve) => {
      resolveInitialisation = resolve;
    });
    const state = await module.loadView(gameId);
    resolveInitialisation();
    this.gameInitialised = true;
    this.gameState[gameId] = state;
    // The events still go out with the re-read view: they carry the action's
    // public facts, which are what the table reads as messages. Only its hidden
    // part is absent from them, and that is what the re-read is for.
    this.subscribers.forEach((c) => c(state, events));
  }

  private handleReceiveEvents(events: GameEvent[]): void {
    // Either the server told us to re-read — a rejoin, or an action that changed
    // something only some players may see — or we hold no state for this game
    // yet. Both mean the view is the authority and the reduce is skipped. The
    // action did take effect, so it resolves here; leaving it pending would let
    // the send time out and repeat it.
    const reload = events.some(
      (event) =>
        event.eventType === RECONNECT_EVENT ||
        event.eventType === REFRESH_EVENT,
    );

    if (reload || !this.gameState[events[0].gameId]) {
      this.resolveExecutes.forEach(([resolve]) => resolve());
      this.resolveExecutes = [];

      // One read per game rather than one per event: a hidden change arrives
      // alongside the events that announced it, and they name the same game.
      Array.from(new Set(events.map((event) => event.gameId))).forEach((gameId) =>
        this.loadGameStateAfterConnect(gameId, events),
      );
    } else {
      this.handleGameEvents(events);
    }
  }

  private async handleGameEvents(events: GameEvent[]) {
    if (!this.gameInitialised && !this.gameInitialisation) {
      throw new Error('Cannot access game before initialised');
    } else if (!this.gameInitialised) {
      await this.gameInitialisation;
    }

    this.resolveExecutes.forEach(([res]) => res());
    this.resolveExecutes = [];

    const id = events[0].gameId;
    const module = this.modules[id];
    if (!module) {
      throw new Error(`No game module registered for game ${id}`);
    }

    const updated = events.reduce(
      (update, event) => module.reduce(update, event),
      this.gameState[id],
    );
    this.gameState[id] = updated;

    if (process.env.NODE_ENV !== 'production') {
      const actualState = await module.loadView(id);
      if (!equal(actualState, updated)) {
        console.error(actualState, updated);
        const [server, client] = differingFields(actualState, updated);
        console.log('State differences (server, client):', server, client);
        this.subscribers.forEach((c) =>
          c(actualState, events, "Updated state doesn't match server state."),
        );
        return;
      }
    }

    this.subscribers.forEach((c) => c(updated, events));
  }
}

export const gameHubService = new GameHubService();
